import React from 'react';

import PropTypes from 'prop-types';

import { Grid } from '@iris/react-components';
import clsx from 'clsx';

import { ListColumns, SubHeading } from 'reaxl';

const variants = {
    tab: {
        containerClass: '',
        headingClass: 'padding-left-2 padding-top-3',
        listClass: 'padding-left-2 padding-top-3',
        sm: 3,
        lg: 3,
        condensed: true,
    },
    modal: {
        containerClass: 'iris:pb-8',
        headingClass: 'padding-bottom-2 padding-top-4',
        listClass: 'padding-left-2',
        gridColumns: { xs: 1, sm: 2, lg: 2, xl: 2 },
    },
};

/**
 * FeatureItem component renders a feature with its values in different contexts (tab or modal).
 *
 * @param {Object} props - The component props
 * @param {string} props.feature - The feature name
 * @param {string[]} props.featureList - Array of feature values
 * @param {string} [props.context='tab'] - The rendering context ('tab' or 'modal')
 * @param {boolean} [props.hasPackages=false] - Whether the parent container has packages (affects border styling in modal)
 * @param {number} [props.index=0] - The index of this feature item (affects border styling in modal)
 * @returns {JSX.Element} The rendered feature item
 */
export default function FeatureItem({ feature, featureList, context = 'tab', hasPackages, index = 0 }) {
    const config = variants[context] || variants.tab;

    // Show border-top logic:
    // - If hasPackages is true: show border-top on all feature items
    // - If hasPackages is false: show border-top on all items EXCEPT the first one (index !== 0)
    const shouldShowBorder = context === 'modal' && (
        hasPackages ? true : index !== 0
    );

    if (context === 'modal') {
        return (
            <div
                className={clsx(config.containerClass, {
                    'iris:border-t-1': shouldShowBorder,
                })}
            >
                <SubHeading
                    className={clsx(config.headingClass, {
                        'padding-bottom-4': shouldShowBorder,
                    })}
                    componentClass="div"
                    level={3}
                    size="300"
                >
                    {feature[0].toUpperCase() + feature.slice(1)}
                </SubHeading>
                <Grid
                    columns={config.gridColumns}
                    className="iris:gap-4"
                >
                    {featureList.map((item, i) => (
                        <span key={`${item}-${i}`}>{item}</span>
                    ))}
                </Grid>
            </div>
        );
    }

    return (
        <div
            key={`${feature}-${index}`}
            className={config.containerClass}
        >
            <ListColumns
                className={config.listClass}
                items={featureList}
                sm={config.sm}
                lg={config.lg}
                condensed={config.condensed}
                expanded={!config.condensed}
            />
        </div>
    );
}

FeatureItem.propTypes = {
    feature: PropTypes.string.isRequired,
    featureList: PropTypes.arrayOf(PropTypes.string).isRequired,
    context: PropTypes.oneOf(['tab', 'modal']),
    hasPackages: PropTypes.bool,
    index: PropTypes.number,
};
